import type { CameraPresetRotation, ExtrudeSvgGraphics3D, LightType, MaterialType } from '@tencent/docs-renderer-3d';
// import { LightTypeNames, MaterialTypeNames } from '@tencent/docs-renderer-3d';
import { Container } from '@tencent/alloy-renderer';
import { BrowserCanvas } from '@tencent/alloy-renderer-browser';
import { ElementBaseComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/base-component/element-base-component';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import { GraphicsType } from '@tencent/alloy-graphics';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { is3DModel, isAttachmentModel, isDomGifPictureModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { IRotation3D } from '@src/slide/domain/ui-model/element-modeles/property/3d/property3d.interface';
import { ignoreRendererFiltersFor3dDisplayObject } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/utils';
import i18n from '@i18next/i18n';
import has from 'lodash/has';

type Extrude3DUpdateProps = ConstructorParameters<typeof ExtrudeSvgGraphics3D>[0];

// todo: 为了修复“部分三维布局后元素整体变黑”，同时避免打包体积过大，临时把变量复制过来。后续需要移除该部分。
const LightTypeNames = {
    balanced: i18n.t('平衡'),
    contrasting: i18n.t('对比'),
    soft: i18n.t('柔和'),
    threePt: i18n.t('三点'),
    twoPt: i18n.t('两点'),
};

const MaterialTypeNames = {
    matte: i18n.t('亚光效果'),
    warmMatte: i18n.t('暖色粗糙'),
    metal: i18n.t('金属'),
    plastic: i18n.t('塑胶'),
};
/**
 * 可复用的 3d 渲染组件
 */
export default class RendererComponent3D extends Container {
    public static isValid3DModel(model?: IBaseModel): boolean {
        return is3DModel(model) && !isDomGifPictureModel(model) && !isAttachmentModel(model);
    }

    public component3D?: ExtrudeSvgGraphics3D;
    public container3D?: Container;
    public baseComponentEvent3D?: ElementBaseComponent;
    private isAsyncAddingChild = false;
    private textureCanvas?: BrowserCanvas;
    private cachedCameraPresetRotation?: typeof CameraPresetRotation;

    public constructor(
        private factoryOptions: InterfaceFactoryOptions<IPictureModel | IShapeModel>,
        private renderUpperTexture?: (textureCanvas: BrowserCanvas) => void,
    ) {
        super();
    }

    public render3D() {
        this.render3DAndRegisterImageLoadedEvent();
    }

    /**
     * @param shouldRegisterImageLoaded 避免无限递归调用，只有第一次调用时需要注册 imageLoaded 事件
     */
    private render3DAndRegisterImageLoadedEvent(shouldRegisterImageLoaded = true): void {
        this.prepareRender3D(shouldRegisterImageLoaded);
        this.renderExtrudeGeometry();
    }

    private prepareRender3D(shouldRegisterImageLoaded: boolean) {
        if (!this.baseComponentEvent3D) {
            this.baseComponentEvent3D = new ElementBaseComponent(this.factoryOptions);
        }
        this.baseComponentEvent3D.renderComponent();
        ignoreRendererFiltersFor3dDisplayObject(this.baseComponentEvent3D?.children[0]);
        if (shouldRegisterImageLoaded) {
            this.baseComponentEvent3D.onImageLoaded(() => {
                this.render3DAndRegisterImageLoadedEvent(false);
            });
        }
        if (!this.textureCanvas) {
            this.textureCanvas = new BrowserCanvas();
        }
        this.textureCanvas.clear();
        this.textureCanvas.width = this.baseComponentEvent3D.width;
        this.textureCanvas.height = this.baseComponentEvent3D.height;
        this.baseComponentEvent3D.render(this.textureCanvas);
        this.renderUpperTexture?.(this.textureCanvas);
    }

    private renderExtrudeGeometry = () => {
        if (!this.baseComponentEvent3D) {
            return;
        }
        const depth = ptToCanvas(this.factoryOptions.model.shape3d?.extrusionHeight ?? 0);
        const { pathArray } = this.baseComponentEvent3D;
        const isPureText = (this.factoryOptions.model as IShapeModel).props.subType === 1;
        const baseWidth = this.baseComponentEvent3D.width;
        const baseHeight = this.baseComponentEvent3D.height;
        const path = pathArray.map(path => ({
            path: path.svgPath,
            scale: [path.scaleX ?? 1, path.scaleY ?? 1] as [number, number],
            isStroke: path.notStroke !== false,
        }));
        const depthFill: any = this.getDepthFill();

        // 获取 Light 预设值，若 LightType 暂未实现（LightTypeNames中为已实现的光源类型），则默认为 threePt
        let lightTypePreset = this.factoryOptions.model.scene3d?.lightRig?.rigPreset as LightType | undefined;
        lightTypePreset = has(LightTypeNames, String(lightTypePreset)) ? lightTypePreset : 'threePt';
        // 同 Light 预设
        let materialPreset = this.factoryOptions.model.shape3d?.materialPreset as MaterialType | undefined;
        materialPreset = has(MaterialTypeNames, String(materialPreset)) ? materialPreset : 'warmMatte';

        const options: Extrude3DUpdateProps = {
            path,
            baseWidth,
            baseHeight,
            depth: this.isFake3DShape((this.factoryOptions.model as IShapeModel).props?.style?.bgShape) ? 0 : depth,
            depthFill,
            rotation3D: this.getRotation3D(),
            // FIXME: 一个是从 sdk 导出的类型，一个是从 renderer-3d 中导出的类型，对不上导致类型报错。这两个类型应该同源
            lightType: lightTypePreset,
            lightRotationAngle: this.factoryOptions.model.scene3d?.lightRig?.rotation?.z,
            // FIXME: ditto
            materialType: materialPreset,
            texture: this.textureCanvas?.native,
            useLight: !isPureText,
            // TODO: @oddguan 棱台渲染还有 bug
            // frontFrustum: {
            //     thickness: this.factoryOptions.model.shape3d?.bevelTop?.height,
            //     width: this.factoryOptions.model.shape3d?.bevelTop?.width,
            // },
            // backFrustum: {
            //     thickness: this.factoryOptions.model.shape3d?.bevelBottom?.height,
            //     width: this.factoryOptions.model.shape3d?.bevelBottom?.width,
            // },
        };
        if (!this.component3D && !this.isAsyncAddingChild) {
            this.asyncLoadGraphics(options);
        } else if (this.component3D) {
            this.component3D.update(options);
        }
    };

    private isFake3DShape(graphicsType: string | undefined): boolean {
        return !!graphicsType && [GraphicsType.CUBE as string].includes(graphicsType);
    }

    private getDepthFill() {
        let color: string | undefined;
        if (this.factoryOptions.model.shape3d?.extrusionColor) {
            color = `#${this.factoryOptions.model.shape3d.extrusionColor.colorValue}`;
        } else {
            color = this.factoryOptions.model.props.style.border?.color ?? undefined;
        }
        if (this.isHexColorWithAlpha(color)) {
            color = color.substring(0, '#ffffff'.length);
        }
        return color;
    }

    private getRotation3D(): IRotation3D {
        const { model } = this.factoryOptions;
        if (model.scene3d?.camera?.preset && model.scene3d.camera.preset !== 'orthographicFront') {
            const result = this.cachedCameraPresetRotation?.[model.scene3d.camera.preset];
            if (result) {
                return this.convertRotationDegreeToRadian(result);
            }
        }
        return this.convertRotationDegreeToRadian(model.scene3d?.camera?.rotation);
    }

    private convertRotationDegreeToRadian(rotation: IRotation3D | undefined): IRotation3D {
        return {
            x: toRadian(rotation?.x ?? 0),
            y: toRadian(rotation?.y ?? 0),
            z: toRadian(rotation?.z ?? 0),
        };
    }

    private async asyncLoadGraphics(options: Extrude3DUpdateProps) {
        this.isAsyncAddingChild = true;
        const rendererModule = await import(/* webpackChunkName: "3d-render" */ '@tencent/docs-renderer-3d');
        this.cachedCameraPresetRotation = rendererModule.CameraPresetRotation;
        options.rotation3D = this.getRotation3D();
        this.component3D = new rendererModule.ExtrudeSvgGraphics3D(options);
        this.component3D.copyBounds(this.baseComponentEvent3D?.initialBounds as any);
        if (!this.children.includes(this.component3D)) {
            this.addChildAt(this.component3D, 0);
            this.isAsyncAddingChild = false;
        }
    }

    private isHexColorWithAlpha(color: string | undefined): color is string {
        return color?.length === '#ffffffff'.length;
    }
}

function toRadian(degree: number) {
    return degree * Math.PI / 180;
}
